import React from "react";
import { getPlayerId } from "../utils/playerIdentity";

const DEFAULT_PLAYER_IMAGE = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const FIELD_ROWS = [
  { key: "wk", label: "WICKET-KEEPERS", aliases: ["wk", "wicketKeepers"] },
  { key: "bat", label: "BATTERS", aliases: ["bat", "batters"] },
  { key: "ar", label: "ALL-ROUNDERS", aliases: ["ar", "allRounders"] },
  { key: "bowl", label: "BOWLERS", aliases: ["bowl", "bowlers"] },
];

const normalizeTeamCode = (value = "") =>
  String(value || "").trim().toUpperCase();

const formatPlayerName = (fullName = "") => {
  const parts = String(fullName || "Player").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] || "Player";
  return `${parts[0][0]} ${parts[parts.length - 1]}`;
};

const getRowPlayers = (groupedPlayers = {}, aliases = []) =>
  aliases.map((alias) => groupedPlayers[alias]).find(Array.isArray) || [];

const isSameSelection = (player = {}, selectionId) =>
  Boolean(selectionId && getPlayerId(player) === String(selectionId));

function TeamPreviewField({
  groupedPlayers = {},
  captainId = null,
  viceCaptainId = null,
  isCaptainPlayer,
  isViceCaptainPlayer,
  getPlayerMetaText,
  getPlayerImage,
  getStatusIndicator,
  teamOneCode = "",
  className = "",
  style = {},
  minHeight = "550px",
}) {
  const renderPlayer = (player, index) => {
    const playerId = getPlayerId(player);
    const playerName = player?.name || player?.playerName || "Player";
    const playerTeamCode = normalizeTeamCode(player?.team);
    const isTeamOne = teamOneCode && playerTeamCode === normalizeTeamCode(teamOneCode);
    const isCaptain = isCaptainPlayer
      ? isCaptainPlayer(player)
      : isSameSelection(player, captainId);
    const isViceCaptain = isViceCaptainPlayer
      ? isViceCaptainPlayer(player)
      : isSameSelection(player, viceCaptainId);
    const statusIndicator = getStatusIndicator ? getStatusIndicator(player) : null;
    const imageSrc =
      getPlayerImage?.(player) ||
      player?.image ||
      DEFAULT_PLAYER_IMAGE;
    const metaText = getPlayerMetaText ? getPlayerMetaText(player) : "";
    const nameTagStyle = isTeamOne
      ? { backgroundColor: "#ffffff", color: "#111827" }
      : { backgroundColor: "#111827", color: "#ffffff" };

    return (
      <div
        key={playerId || `${playerName}_${index}`}
        className="d-flex flex-column align-items-center position-relative"
        style={{ width: "78px", zIndex: 10 }}
      >
        <div className="position-relative d-flex justify-content-center align-items-end" style={{ width: "58px", height: "58px" }}>
          {(isCaptain || isViceCaptain) && (
            <span
              className="position-absolute rounded-circle d-flex align-items-center justify-content-center fw-bold"
              style={{
                top: "-8px",
                left: "-8px",
                width: "20px",
                height: "20px",
                zIndex: 20,
                backgroundColor: "#050505",
                color: "#ffffff",
                border: "1px solid #ffffff",
                fontSize: isCaptain ? "9px" : "8px",
                lineHeight: 1,
                boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
              }}
            >
              {isCaptain ? "C" : "VC"}
            </span>
          )}

          {statusIndicator && (
            <span
              className="position-absolute rounded-circle"
              style={{
                top: "-2px",
                right: "-2px",
                width: "10px",
                height: "10px",
                zIndex: 20,
                border: "2px solid #ffffff",
                backgroundColor: statusIndicator.color,
              }}
              title={statusIndicator.label}
            ></span>
          )}

          <img
            src={imageSrc}
            alt={playerName}
            onError={(event) => {
              event.currentTarget.src = DEFAULT_PLAYER_IMAGE;
            }}
            style={{
              width: "56px",
              height: "56px",
              objectFit: "contain",
              objectPosition: "bottom",
              filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.32))",
            }}
          />
        </div>

        <div
          className="text-center fw-bold text-truncate shadow-sm text-uppercase"
          style={{
            ...nameTagStyle,
            maxWidth: "76px",
            minWidth: "58px",
            padding: "2px 8px",
            borderRadius: "3px",
            fontSize: "10px",
            lineHeight: "14px",
            marginTop: "-5px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={playerName}
        >
          {formatPlayerName(playerName)}
        </div>

        {metaText && (
          <div
            className="text-white text-center"
            style={{
              fontSize: "10px",
              fontWeight: 600,
              marginTop: "2px",
              opacity: 0.9,
              lineHeight: "12px",
              textShadow: "0 1px 3px rgba(0,0,0,0.55)",
            }}
          >
            {metaText}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`position-relative overflow-hidden d-flex flex-column justify-content-evenly py-3 ${className}`.trim()}
      style={{
        minHeight,
        background:
          "radial-gradient(circle at 50% 15%, rgba(255,255,255,0.18) 0, rgba(255,255,255,0) 28%), linear-gradient(180deg, #15803d 0%, #22c55e 46%, #166534 100%)",
        ...style,
      }}
    >
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.055) 0, rgba(255,255,255,0.055) 1px, transparent 1px, transparent 18px), repeating-linear-gradient(0deg, rgba(0,0,0,0.055) 0, rgba(0,0,0,0.055) 1px, transparent 1px, transparent 22px)",
          opacity: 0.35,
          pointerEvents: "none",
        }}
      ></div>

      <div
        className="position-absolute top-50 start-50 translate-middle rounded-4"
        style={{
          width: "90px",
          height: "78%",
          background: "linear-gradient(180deg, rgba(216,188,128,0.9) 0%, rgba(177,139,73,0.9) 100%)",
          border: "1px solid rgba(255,255,255,0.32)",
          boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.12), 0 16px 40px rgba(0,0,0,0.18)",
          opacity: 0.78,
          pointerEvents: "none",
          zIndex: 1,
        }}
      ></div>

      <div
        className="position-absolute top-50 start-50 translate-middle rounded-circle"
        style={{
          width: "78%",
          height: "42%",
          border: "1px solid rgba(255,255,255,0.14)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      ></div>

      <div className="position-relative d-flex flex-column justify-content-evenly flex-grow-1" style={{ zIndex: 2 }}>
        {FIELD_ROWS.map((row) => {
          const players = getRowPlayers(groupedPlayers, row.aliases);
          if (players.length === 0) return null;

          return (
            <div key={row.key} className="text-center w-100 d-flex flex-column align-items-center justify-content-center px-2 py-1">
              <div
                className="text-white fw-bold mb-2"
                style={{ fontSize: "10px", letterSpacing: "1px", opacity: 0.88, textShadow: "0 1px 3px rgba(0,0,0,0.45)" }}
              >
                {row.label}
              </div>
              <div className="d-flex justify-content-center flex-wrap align-items-end" style={{ gap: "14px 20px" }}>
                {players.map(renderPlayer)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TeamPreviewField;
